import { useEffect, useRef } from 'react';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import agentAccessApi from '@api/agentAccessApi';
import { calendarActions } from '@state/calendar';
import { eventActions } from '@state/events';
import { workloadActions } from '@state/workload';
import { settingsActions } from '@state/settings';
import { changedElsewhere } from '@state/pile/actions';
import { semesterSelectors } from '@state/semester';
import { clearEvents } from '@state/events/actions';
import { onAppStart } from '@state/common/actions';
import { undoActions } from '@state/undo';

// While the tab is on screen (e.g. next to the AI app's chat), how often to ask whether the app changed something.
const POLL_MS = 15_000;
const RETURN_GAP_MS = 2_000;

/**
 * Reads again everything an AI app can change: tasks, sessions, events, courses, activities, settings. Undo's history
 * goes: what it kept may have been changed by the app, and an Undo now would take back the app's change too.
 */
export const reloadAfterAgentChange = (dispatch: Dispatch) => {
  dispatch(undoActions.clear());
  dispatch(calendarActions.commitmentsData.request());
  dispatch(calendarActions.calendarData.request());
  dispatch(eventActions.sourceEvents.request());
  dispatch(eventActions.getEventsList.request({}));
  dispatch(workloadActions.getWorkload.request(undefined));
  dispatch(settingsActions.getUserSettings.request());
  dispatch(changedElsewhere());
};

/** The app's own term switch (Settings → Terms), minus the server call the AI app has already made. */
const switchToCurrentTerm = (dispatch: Dispatch) => {
  dispatch(clearEvents());
  dispatch(calendarActions.clearState());
  dispatch(onAppStart({ location: { pathname: '/' } }));
};

/**
 * Changes an AI app makes show up without a manual reload: Shovel asks when an app last changed something
 * every 15 seconds while the tab is visible, and as soon as the student comes back to it.
 * Without a connected app it only asks when the student comes back (they may have just connected one).
 */
export function useAgentChangeRefresh(active: boolean) {
  const dispatch = useDispatch();
  const semesterId = useSelector(semesterSelectors.getId);
  const shownTerm = useRef(semesterId);
  shownTerm.current = semesterId;

  useEffect(() => {
    if (!active) return;

    let seen: string | null | undefined;
    let connected = false;
    let interval: number | undefined;
    let stopped = false;
    let lastAsked = 0;

    const check = async () => {
      lastAsked = Date.now();
      const response = await agentAccessApi.getLastChange();
      if (stopped || !response.ok || !response.data) return;
      const { lastChangeAt = null } = response.data;
      // The first answer is only where we start from: what's on screen was loaded after it.
      if (seen !== undefined && lastChangeAt !== seen) {
        const { currentSemesterId } = response.data;
        if (currentSemesterId && shownTerm.current && currentSemesterId !== shownTerm.current) {
          switchToCurrentTerm(dispatch);
        } else {
          reloadAfterAgentChange(dispatch);
        }
      }
      seen = lastChangeAt;
      connected = response.data.connected;
      schedule();
    };

    const schedule = () => {
      const poll = connected && document.visibilityState === 'visible';
      if (poll && interval === undefined) {
        interval = window.setInterval(check, POLL_MS);
      } else if (!poll && interval !== undefined) {
        window.clearInterval(interval);
        interval = undefined;
      }
    };

    // Coming back fires both "visible" and "focus": one check is enough.
    const onReturn = () => {
      if (document.visibilityState === 'visible') {
        if (Date.now() - lastAsked > RETURN_GAP_MS) check();
        else schedule();
      } else {
        schedule();
      }
    };

    check();
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      stopped = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [active]);
}
